import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { fetchPlanRecord, type PlanRecord } from '../../IndividualPlan/planRecordApi';
import { formatPlanMonthDay } from '../../IndividualPlan/planRecordView';
import { CARD_TEXT, Chip, ProfileCard } from './ProfileCard';
import { currentPlanOf, displayYear, exitDate, longDate, noPlanReason, PLANS_STATUS_UNREAD, sittingAfterExit } from '../profileText';
import type { StudentProfile } from '../studentProfileApi';
import { ROSTER } from '../tokens';

type Plan = StudentProfile['plans'][number];
type RecordSource = (studentId: string, schoolYear: string) => Promise<PlanRecord>;
type Shown = { chip?: { label: string; tone: 'neutral' | 'active' }; lines: string[] };
// This year's entry when no saved plan answers for it: why there is none, or,
// for an active student whose plans are released, the plan waiting to be opened.
type YearEntry = { chip: string; text: string; waiting: boolean };

const LINE = { m: 0, fontFamily: ROSTER.font, fontSize: 16, fontWeight: 500, lineHeight: '20px', color: ROSTER.muted } as const;
const VIEW_SX = { height: 36, borderRadius: '8px', borderColor: ROSTER.border, color: ROSTER.link, textTransform: 'none', fontFamily: ROSTER.font, fontSize: 16, fontWeight: 600, lineHeight: '20px', '&:hover': { borderColor: ROSTER.border, bgcolor: ROSTER.hoverBg } } as const;

function newestPlan(profile: StudentProfile): Plan | null {
  return profile.plans.reduce<Plan | null>((best, plan) => (best && best.schoolYear >= plan.schoolYear ? best : plan), null);
}

function yearEntry(profile: StudentProfile): YearEntry | null {
  if (profile.standing !== 'active') {
    if (profile.standing === 'departed' || profile.standing === 'unknown') return null;
    if (profile.standing === 'waived') return { chip: 'No plan', text: 'EL services are waived; no plan is generated while they are.', waiting: false };
    const { exitYearEnd, exitMonth } = profile.elStatus;
    const since = exitYearEnd ? ` in ${exitDate(exitYearEnd, exitMonth ?? 0)}` : '';
    const newest = profile.access.administrations[0];
    const testedSince = !exitYearEnd || (newest != null && sittingAfterExit(newest, exitYearEnd, exitMonth ?? 0));
    const standing = profile.standing === 'monitoring' ? 'Monitoring' : 'Former EL';
    const text = testedSince ? `Exited${since}; no new plan is generated for a ${standing} student.` : `Not tested since exiting${since}, so no plan is generated.`;
    return { chip: 'No plan', text, waiting: false };
  }
  const teacher = profile.role === 'teacher';
  const reason = noPlanReason({ inFile: profile.access.inFile, noScores: profile.access.noScores, plansGenerated: profile.plansGenerated, plansGeneratedKnown: profile.plansGeneratedKnown, year: profile.currentSchoolYear, teacher });
  if (reason) return { chip: !profile.access.inFile || profile.access.noScores ? 'No plan' : reason === PLANS_STATUS_UNREAD ? 'Unknown' : 'Not generated', text: reason, waiting: false };
  return { chip: 'Not opened yet', text: teacher ? 'Your EL office has not opened this year’s plan yet' : 'Open it to generate this year’s plan', waiting: true };
}

function shownOf(plan: Plan, record: PlanRecord | null, teacher: boolean): Shown {
  const generated = `Generated ${longDate(plan.generatedAt)}`;
  if (!record) return { lines: [generated] };
  if (record.version.status !== 'finalized') return { chip: { label: 'Draft', tone: 'neutral' }, lines: [generated] };
  const by = record.version.finalizedBy?.name;
  const lines = [`Finalized ${longDate(record.version.finalizedAt)}${by ? ` by ${by}` : ''}`];
  const reviewers = record.expectedReviewers.length;
  const reviewed = record.expectedReviewers.filter((r) => r.status === 'reviewed').length;
  const tally = reviewers > 0 ? `${reviewed} of ${reviewers} teacher${reviewers === 1 ? '' : 's'}` : '';
  const mine = teacher && record.mySignOff?.version === record.version.number ? record.mySignOff : null;
  if (mine) lines.push([`You signed off ${formatPlanMonthDay(mine.signedAt)}`, tally].filter(Boolean).join(' · '));
  else if (tally) lines.push(`Signed off by ${tally}`);
  return { chip: { label: 'Finalized', tone: 'active' }, lines };
}

function YearRow({ year, chip }: { year: string; chip?: Shown['chip'] }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
      <Typography component="span" sx={CARD_TEXT.strong}>{`SY ${displayYear(year)}`}</Typography>
      {chip && <Chip tone={chip.tone}>{chip.label}</Chip>}
    </Box>
  );
}

function ViewButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outlined" fullWidth onClick={onClick} sx={VIEW_SX}>
      View student plan
    </Button>
  );
}

function SavedPlan({ plan, onOpen, recordSource, teacher }: { plan: Plan; onOpen?: () => void; recordSource: RecordSource; teacher: boolean }) {
  const key = `${plan.studentId}|${plan.schoolYear}`;
  const [loaded, setLoaded] = useState<{ key: string; record: PlanRecord | null } | null>(null);
  useEffect(() => {
    let active = true;
    recordSource(plan.studentId, plan.schoolYear)
      .then((record) => {
        if (active) setLoaded({ key, record });
      })
      .catch(() => {
        if (active) setLoaded({ key, record: null });
      });
    return () => {
      active = false;
    };
  }, [key, plan.studentId, plan.schoolYear, recordSource]);
  const shown = shownOf(plan, loaded?.key === key ? loaded.record : null, teacher);
  return (
    <>
      <YearRow year={plan.schoolYear} chip={shown.chip} />
      {shown.lines.map((line) => (
        <Typography key={line} sx={LINE}>{line}</Typography>
      ))}
      {onOpen && <ViewButton onClick={onOpen} />}
    </>
  );
}

// One plan, the newest on file, so the way into it is a single button. The plan
// page's year menu reaches the older ones. While this year's plan waits to be
// opened, that comes first: opening it is what generates it.
export function ElPlansCard({ profile, onOpenPlan, recordSource = fetchPlanRecord }: { profile: StudentProfile; onOpenPlan: (studentId: string, schoolYear?: string) => void; recordSource?: RecordSource }) {
  const admin = profile.role !== 'teacher';
  const newest = profile.plansKnown ? newestPlan(profile) : null;
  const entry = profile.plansKnown ? yearEntry(profile) : null;
  const waiting = entry?.waiting === true && !currentPlanOf(profile) && (admin || !newest);
  let body;
  if (!profile.plansKnown) {
    body = <Typography sx={CARD_TEXT.small}>Plans could not be loaded. Refresh the page to try again.</Typography>;
  } else if (newest && !waiting) {
    const currentPlan = newest.schoolYear === profile.currentSchoolYear && profile.standing === 'active';
    const open = () => (currentPlan ? onOpenPlan(newest.studentId) : onOpenPlan(newest.studentId, newest.schoolYear));
    const unreleasedForTeacher = !admin && (newest.schoolYear > profile.currentSchoolYear || (newest.schoolYear === profile.currentSchoolYear && !profile.plansGenerated));
    body = <SavedPlan plan={newest} onOpen={unreleasedForTeacher ? undefined : open} recordSource={recordSource} teacher={!admin} />;
  } else if (entry) {
    body = (
      <>
        <YearRow year={profile.currentSchoolYear} chip={{ label: entry.chip, tone: 'neutral' }} />
        <Typography sx={LINE}>{entry.text}</Typography>
        {entry.waiting && admin && <ViewButton onClick={() => onOpenPlan(profile.student.id)} />}
      </>
    );
  } else {
    body = <Typography sx={CARD_TEXT.small}>No plans on file.</Typography>;
  }
  return <ProfileCard title="Student Action Plan">{body}</ProfileCard>;
}
